import { termSpells } from "../../engine/adventure/ActiveAdventure.js";
import React, { useState } from "react";
import { PartyStore } from "@store/PartyStore.js";
import { abilityModifier } from "@engine/types/entity.js";
import type {
  PlayerCharacter,
  AbilityName,
  Character,
} from "@engine/types/entity.js";

interface Props {
  onBack: () => void;
}

const ABILITIES: readonly AbilityName[] = ["STR", "DEX", "CON", "INT", "WIS", "CHA"];

/**
 * U-series / C14 — read-only character sheet viewer.
 *
 * Shown from `MainMenu` → "Character Sheet". Per-member tabs. No editing;
 * future respec / rename work is explicitly deferred (see
 * `specs/party-and-characters.md` §Out of scope for C13/C14).
 */
export function CharacterSheet({ onBack }: Props) {
  const members = PartyStore.getMembers();
  const [activeIdx, setActiveIdx] = useState(0);

  if (members.length === 0) {
    return (
      <div style={containerStyle}>
        <h2 style={titleStyle}>CHARACTER SHEET</h2>
        <p style={{ color: "#ccc", fontSize: 13, margin: 0 }}>
          No party members. Start a new game to create characters.
        </p>
        <BackButton onBack={onBack} />
      </div>
    );
  }

  const clampedIdx = activeIdx < members.length ? activeIdx : 0;
  const member = members[clampedIdx]!;

  return (
    <div style={containerStyle}>
      <h2 style={titleStyle}>CHARACTER SHEET</h2>

      {members.length > 1 && (
        <div style={tabBarStyle} role="tablist">
          {members.map((m, i) => (
            <button
              key={m.instanceId}
              role="tab"
              aria-selected={i === clampedIdx}
              onClick={() => setActiveIdx(i)}
              style={{
                ...tabStyle,
                background: i === clampedIdx ? "#2a6" : "#333",
                borderColor: i === clampedIdx ? "#6cf" : "rgba(255,255,255,0.2)",
              }}
            >
              {m.name}
            </button>
          ))}
        </div>
      )}

      <MemberPane member={member} />

      <BackButton onBack={onBack} />
    </div>
  );
}

export function MemberPane({ member }: { member: PlayerCharacter }) {
  const asChar = member as Partial<Character> & PlayerCharacter;
  const portrait = asChar.portrait ?? null;
  const race = asChar.race ?? "—";
  const background = asChar.background ?? "—";
  const known = member.knownSpells ?? [];
  const reactions = member.reactions ?? [];
  const skills = asChar.skillProficiencies ?? new Set<string>();
  const saves = asChar.saveProficiencies ?? new Set<AbilityName>();

  return (
    <div data-testid={`sheet-pane-${member.instanceId}`} style={paneStyle}>
      <div style={{ display: "flex", gap: 12 }}>
        <div style={portraitSlotStyle}>
          {portrait ? (
            <img
              src={portrait}
              alt={`${member.name} portrait`}
              style={{ width: "100%", height: "100%", objectFit: "cover" }}
            />
          ) : (
            <span style={{ fontSize: 10, color: "#888" }}>no portrait</span>
          )}
        </div>
        <dl style={dlStyle}>
          <dt style={dtStyle}>Name</dt>
          <dd style={ddStyle}>{member.name}</dd>
          <dt style={dtStyle}>Race</dt>
          <dd style={ddStyle}>{race}</dd>
          <dt style={dtStyle}>Class</dt>
          <dd style={ddStyle}>{member.characterClass}</dd>
          <dt style={dtStyle}>Background</dt>
          <dd style={ddStyle}>{background}</dd>
          <dt style={dtStyle}>Level</dt>
          <dd style={ddStyle}>
            {member.level} ({member.xp ?? 0} XP)
          </dd>
          <dt style={dtStyle}>HP</dt>
          <dd style={ddStyle}>
            {member.hp} / {member.maxHp}
          </dd>
          <dt style={dtStyle}>AC</dt>
          <dd style={ddStyle}>{member.armorClass}</dd>
        </dl>
      </div>

      <section>
        <h3 style={sectionTitleStyle}>Abilities</h3>
        <div style={abilityGridStyle}>
          {ABILITIES.map((name) => {
            const score = member.abilities[name];
            const mod = abilityModifier(score);
            const saveProf = saves.has(name);
            return (
              <div key={name} style={abilityCellStyle}>
                <div style={{ fontSize: 10, color: "#888" }}>
                  {name}
                  {saveProf && <span style={{ marginLeft: 4, color: "#6cf" }}>◆</span>}
                </div>
                <div style={{ fontSize: 16 }}>{score}</div>
                <div style={{ fontSize: 11, color: "#ccc" }}>
                  {mod >= 0 ? `+${mod}` : mod}
                </div>
              </div>
            );
          })}
        </div>
      </section>

      {skills.size > 0 && (
        <section>
          <h3 style={sectionTitleStyle}>Skill proficiencies</h3>
          <div style={{ fontSize: 12, color: "#ddd" }}>
            {[...skills].join(", ")}
          </div>
        </section>
      )}

      {known.length > 0 && (
        <section>
          <h3 style={sectionTitleStyle}>Known {termSpells()}</h3>
          <div style={{ fontSize: 12, color: "#ddd" }}>{known.join(", ")}</div>
        </section>
      )}

      {reactions.length > 0 && (
        <section>
          <h3 style={sectionTitleStyle}>Reactions</h3>
          <div style={{ fontSize: 12, color: "#ddd" }}>{reactions.join(", ")}</div>
        </section>
      )}
    </div>
  );
}

function BackButton({ onBack }: { onBack: () => void }) {
  return (
    <button onClick={onBack} style={backBtnStyle}>
      Back
    </button>
  );
}

// ── Styles ───────────────────────────────────────────────────────────────────

const containerStyle: React.CSSProperties = {
  display: "flex",
  flexDirection: "column",
  gap: 12,
  color: "white",
  fontFamily: "monospace",
  width: "100%",
  maxWidth: 480,
};

const titleStyle: React.CSSProperties = {
  margin: 0,
  fontSize: 18,
  letterSpacing: 2,
};

const tabBarStyle: React.CSSProperties = {
  display: "flex",
  gap: 4,
  flexWrap: "wrap",
};

const tabStyle: React.CSSProperties = {
  padding: "4px 10px",
  color: "white",
  border: "1px solid",
  borderRadius: 4,
  fontFamily: "monospace",
  fontSize: 12,
  cursor: "pointer",
};

const paneStyle: React.CSSProperties = {
  display: "flex",
  flexDirection: "column",
  gap: 12,
};

const portraitSlotStyle: React.CSSProperties = {
  width: 96,
  height: 96,
  background: "#c9c1b0",
  border: "1px solid rgba(255,255,255,0.2)",
  borderRadius: 4,
  overflow: "hidden",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  flexShrink: 0,
};

const dlStyle: React.CSSProperties = {
  display: "grid",
  gridTemplateColumns: "auto 1fr",
  gap: "4px 12px",
  margin: 0,
  fontSize: 13,
  flex: 1,
};

const dtStyle: React.CSSProperties = { color: "rgba(255,255,255,0.6)" };
const ddStyle: React.CSSProperties = { margin: 0, color: "white" };

const sectionTitleStyle: React.CSSProperties = {
  margin: "0 0 4px 0",
  fontSize: 11,
  letterSpacing: 1,
  color: "rgba(255,255,255,0.6)",
  textTransform: "uppercase",
};

const abilityGridStyle: React.CSSProperties = {
  display: "grid",
  gridTemplateColumns: "repeat(6, 1fr)",
  gap: 4,
};

const abilityCellStyle: React.CSSProperties = {
  background: "#222",
  border: "1px solid rgba(255,255,255,0.2)",
  borderRadius: 4,
  padding: "6px 4px",
  textAlign: "center",
};

const backBtnStyle: React.CSSProperties = {
  background: "#333",
  color: "white",
  border: "1px solid rgba(255,255,255,0.3)",
  borderRadius: 4,
  padding: "8px 24px",
  fontFamily: "monospace",
  fontSize: 13,
  cursor: "pointer",
  alignSelf: "center",
  marginTop: 8,
};
